import { useNavigate } from 'react-router-dom';
import { X, MoveRight, LogOut } from 'lucide-react';
import { cn } from '../../utils/cn';
import { mainNavigation } from './navigation';
import { NavGroup } from './NavGroup';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { useMemo } from 'react';
import { getStyretNavItems } from '../Styret/StyretSidebar';
import { getAdminNavItems } from '../Admin/AdminSidebar';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const { currentResidence, residences, isAdmin } = useUser();
  const { logout } = useAuth();

  // Filtrer navigasjonslenker basert på brukerens tilgang (samme logikk som NavHeader)
  const shouldShowNavItem = (item: typeof mainNavigation[0]) => {
    if (!item.roles) return true;
    
    return item.roles.some(role => {
      if (role === 'admin') return isAdmin;
      if (role === 'board') {
        // Sjekk om brukeren har en board-rolle i noen av sine tilhørigheter
        return isAdmin || currentResidence?.role === 'board' || residences.some(r => r.role === 'board');
      }
      // For owner/tenant, sjekk currentResidence eller alle residences
      return currentResidence?.role === role || residences.some(r => r.role === role);
    });
  };

  // Bygg navigasjon med dynamiske ruter fra sidebars
  const filteredNavigation = useMemo(() => {
    const residenceId = currentResidence?.residence_id;
    const residenceType = currentResidence?.residence?.type;
    
    // Sjekk om brukeren har board-rolle i noen tilhørighet
    const hasAnyBoardRole = isAdmin || currentResidence?.role === 'board' || residences.some(r => r.role === 'board');
    
    return mainNavigation
      .filter(shouldShowNavItem)
      .map(item => {
        // Bruk getStyretNavItems for Styret-navigasjon
        if (item.label === 'Styret' && hasAnyBoardRole) {
          const styretItems = getStyretNavItems(residenceId, residenceType);
          return {
            ...item,
            children: styretItems.map(styretItem => ({
              to: styretItem.href,
              label: styretItem.name
            }))
          };
        }
        
        // Bruk getAdminNavItems for Admin-navigasjon
        if (item.label === 'Admin' && isAdmin) {
          const adminItems = getAdminNavItems();
          return {
            ...item,
            children: adminItems.map(adminItem => ({
              to: adminItem.to,
              label: adminItem.label
            }))
          };
        }
        
        return item;
      });
  }, [currentResidence?.residence_id, currentResidence?.residence?.type, currentResidence?.role, isAdmin, residences]);

  const handleLogoClick = () => {
    navigate('/');
    onClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/* Mobile sidebar */}
      <div
        className={cn(
          "fixed inset-0 z-50 lg:hidden",
          isOpen ? "translate-x-0" : "-translate-x-full",
          "transition-transform duration-300 ease-in-out"
        )}
      >
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black/50"
          onClick={onClose}
        />

        {/* Sidebar content */}
        <div className="relative h-full w-full bg-condo-dark">
          {/* Header */}
          <div className="flex h-16 items-center justify-between p-8 py-12">
            <button
              onClick={handleLogoClick}
              className="focus:outline-none focus:ring-2 focus:ring-condo-light focus:ring-offset-2 focus:ring-offset-condo-dark rounded-lg"
            >
              <img 
                src="/images/Condo-logo-light.png" 
                alt="Condo" 
                className="h-8"
              />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-condo-light hover:text-white transition-colors"
              aria-label="Lukk meny"
            >
              <X className="h-8 w-8" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex flex-col h-[calc(100%-4rem)] pt-8">
            {/* Main navigation med Profil */}
            <div className="flex-1 px-8 space-y-8">
              {filteredNavigation.map((item) => (
                <NavGroup key={item.to} item={item} onClick={() => handleNavigation(item.to)} />
              ))}
              
              {/* Profil rett etter siste nav-element */}
              <button
                onClick={() => handleNavigation('/profil')}
                className={cn(
                  "w-full flex items-center gap-4",
                  "text-condo-light hover:text-white transition-colors",
                  "focus:outline-none focus:ring-2 focus:ring-condo-light focus:ring-offset-2 focus:ring-offset-condo-dark"
                )}
              >
                <MoveRight className="h-6 w-6" />
                <span className="text-2xl font-light">Profil</span>
              </button>
            </div>

            {/* Logg ut nederst */}
            <div className="px-8 pb-12 border-t border-condo-light/20 pt-6">
              <button
                onClick={async () => {
                  await logout();
                  onClose();
                  // logout() i AuthContext redirecter til /landing
                }}
                className={cn(
                  "w-full flex items-center gap-4",
                  "text-condo-light/80 hover:text-red-400 transition-colors",
                  "focus:outline-none focus:ring-2 focus:ring-condo-light focus:ring-offset-2 focus:ring-offset-condo-dark"
                )}
              >
                <LogOut className="h-6 w-6" />
                <span className="text-2xl font-light">Logg ut</span>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}